// LocationDeepLinks — forward links from /locations/$slug (and EN mirror)
// to deep service × location combo URLs. Densifies the internal-link mesh
// so the location hub funnels crawlers directly into long-tail combo pages
// (translate × language × location, notary × act × location, police × type × location).
import { Link } from "@tanstack/react-router";
import { Languages, Stamp, ShieldCheck, ArrowRight } from "lucide-react";
import { getLocation } from "@/data/locations";
import { POLICE_COMBO_LOC_SLUGS } from "@/routes/police-clearance.$type.index";

// EN combo allow-list mirrors en.translate / en.notary route filters
const EN_LOCATION_TYPES = new Set([
  "headquarters",
  "branch",
  "bkk-district",
  "transit",
  "mall",
  "expat-hub",
]);

// Curated picks — high-intent, evergreen
const TOP_LANGS: { slug: string; nameTh: string; nameEn: string }[] = [
  { slug: "english", nameTh: "อังกฤษ", nameEn: "English" },
  { slug: "japanese", nameTh: "ญี่ปุ่น", nameEn: "Japanese" },
  { slug: "chinese", nameTh: "จีน", nameEn: "Chinese" },
  { slug: "korean", nameTh: "เกาหลี", nameEn: "Korean" },
  { slug: "german", nameTh: "เยอรมัน", nameEn: "German" },
  { slug: "french", nameTh: "ฝรั่งเศส", nameEn: "French" },
];

const TOP_ACTS: { slug: string; nameTh: string; nameEn: string }[] = [
  { slug: "affidavit", nameTh: "Affidavit (คำให้การ)", nameEn: "Affidavit" },
  { slug: "certified-true-copy", nameTh: "รับรองสำเนาถูกต้อง", nameEn: "Certified True Copy" },
  { slug: "certify-signature", nameTh: "รับรองลายมือชื่อ", nameEn: "Certify Signature" },
  { slug: "statutory-declaration", nameTh: "Statutory Declaration", nameEn: "Statutory Declaration" },
];

const TOP_POLICE: { slug: string; nameTh: string; nameEn: string }[] = [
  { slug: "pcsc-thailand-local", nameTh: "ตรวจประวัติ (ใช้ในไทย)", nameEn: "Police Clearance (Domestic)" },
  { slug: "pcsc-thailand-abroad", nameTh: "ตรวจประวัติ (ใช้ต่างประเทศ)", nameEn: "Police Clearance (Overseas)" },
  { slug: "pcsc-poa-overseas", nameTh: "PCSC แบบมอบอำนาจ", nameEn: "PCSC by Power of Attorney" },
];

type Props = { locationSlug: string; locale: "th" | "en" };

export function LocationDeepLinks({ locationSlug, locale }: Props) {
  const loc = getLocation(locationSlug);
  if (!loc) return null;

  const isEn = locale === "en";
  if (isEn && !EN_LOCATION_TYPES.has(loc.type)) return null;

  const policeOk = (POLICE_COMBO_LOC_SLUGS as readonly string[]).includes(loc.slug);
  const locName = isEn ? loc.nameEn : loc.nameTh;

  const headings = isEn
    ? {
        section: `Specialized services in ${locName}`,
        sub: `Direct links to the most-requested language, notarial act, and police clearance combos at this location.`,
        translate: `Certified translation in ${locName}`,
        notary: `Notary Public acts in ${locName}`,
        police: `Police clearance in ${locName}`,
      }
    : {
        section: `บริการเฉพาะทางใน${locName}`,
        sub: `ลิงก์ตรงสู่ภาษา · งานโนตารี · ตรวจประวัติ ที่ลูกค้าใน${locName}ขอบ่อยที่สุด`,
        translate: `แปลรับรองยอดนิยมใน${locName}`,
        notary: `งาน Notary Public ใน${locName}`,
        police: `ตรวจประวัติอาชญากรรมใน${locName}`,
      };

  return (
    <section className={`border-t px-4 py-14 sm:px-6 sm:py-16 ${isEn ? "bg-white border-slate-200" : "border-white/10"}`}>
      <div className="mx-auto max-w-5xl">
        <h2 className={`font-display text-2xl font-extrabold sm:text-3xl ${isEn ? "text-navy-deep" : "text-gold"}`}>
          {headings.section}
        </h2>
        <p className={`mt-2 text-sm ${isEn ? "text-slate-600" : "text-white/70"}`}>{headings.sub}</p>

        {/* Translate combos */}
        <div className="mt-8">
          <h3 className={`flex items-center gap-2 text-xs font-bold uppercase tracking-widest ${isEn ? "text-amber-700" : "text-gold/80"}`}>
            <Languages className="h-3.5 w-3.5" /> {headings.translate}
          </h3>
          <ul className="mt-3 grid gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
            {TOP_LANGS.map((l) => (
              <li key={l.slug}>
                {isEn ? (
                  <Link
                    to="/en/translate/$language/in/$location"
                    params={{ language: l.slug, location: loc.slug }}
                    className="group flex items-center justify-between gap-2 rounded-xl border border-slate-200 bg-slate-50/60 px-3.5 py-2.5 text-sm text-slate-800 transition hover:-translate-y-0.5 hover:border-amber-400/60 hover:bg-white hover:shadow-sm"
                  >
                    <span className="font-semibold">{l.nameEn} · {loc.nameEn}</span>
                    <ArrowRight className="h-3.5 w-3.5 shrink-0 text-slate-400 transition group-hover:translate-x-0.5 group-hover:text-amber-600" />
                  </Link>
                ) : (
                  <Link
                    to="/translate/$language/in/$location"
                    params={{ language: l.slug, location: loc.slug }}
                    className="group flex items-center justify-between gap-2 rounded-xl border border-white/10 bg-white/[0.04] px-3.5 py-2.5 text-sm text-white/90 transition hover:-translate-y-0.5 hover:border-gold/40 hover:bg-white/[0.08]"
                  >
                    <span className="font-semibold">แปล{l.nameTh} · {loc.nameTh}</span>
                    <ArrowRight className="h-3.5 w-3.5 shrink-0 text-white/40 transition group-hover:translate-x-0.5 group-hover:text-gold" />
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </div>

        {/* Notary combos */}
        <div className="mt-8">
          <h3 className={`flex items-center gap-2 text-xs font-bold uppercase tracking-widest ${isEn ? "text-amber-700" : "text-gold/80"}`}>
            <Stamp className="h-3.5 w-3.5" /> {headings.notary}
          </h3>
          <ul className="mt-3 grid gap-2.5 sm:grid-cols-2">
            {TOP_ACTS.map((a) => (
              <li key={a.slug}>
                {isEn ? (
                  <Link
                    to="/en/notary/$act/in/$location"
                    params={{ act: a.slug, location: loc.slug }}
                    className="group flex items-center justify-between gap-2 rounded-xl border border-slate-200 bg-slate-50/60 px-3.5 py-2.5 text-sm text-slate-800 transition hover:-translate-y-0.5 hover:border-amber-400/60 hover:bg-white hover:shadow-sm"
                  >
                    <span className="font-semibold">{a.nameEn} · {loc.nameEn}</span>
                    <ArrowRight className="h-3.5 w-3.5 shrink-0 text-slate-400 transition group-hover:translate-x-0.5 group-hover:text-amber-600" />
                  </Link>
                ) : (
                  <Link
                    to="/notary/$act/in/$location"
                    params={{ act: a.slug, location: loc.slug }}
                    className="group flex items-center justify-between gap-2 rounded-xl border border-white/10 bg-white/[0.04] px-3.5 py-2.5 text-sm text-white/90 transition hover:-translate-y-0.5 hover:border-gold/40 hover:bg-white/[0.08]"
                  >
                    <span className="font-semibold">{a.nameTh} · {loc.nameTh}</span>
                    <ArrowRight className="h-3.5 w-3.5 shrink-0 text-white/40 transition group-hover:translate-x-0.5 group-hover:text-gold" />
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </div>

        {/* Police combos — TH route only (no EN mirror exists) */}
        {!isEn && policeOk && (
          <div className="mt-8">
            <h3 className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-gold/80">
              <ShieldCheck className="h-3.5 w-3.5" /> {headings.police}
            </h3>
            <ul className="mt-3 grid gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
              {TOP_POLICE.map((p) => (
                <li key={p.slug}>
                  <Link
                    to="/police-clearance/$type/in/$location"
                    params={{ type: p.slug, location: loc.slug }}
                    className="group flex items-center justify-between gap-2 rounded-xl border border-white/10 bg-white/[0.04] px-3.5 py-2.5 text-sm text-white/90 transition hover:-translate-y-0.5 hover:border-gold/40 hover:bg-white/[0.08]"
                  >
                    <span className="font-semibold">{p.nameTh} · {loc.nameTh}</span>
                    <ArrowRight className="h-3.5 w-3.5 shrink-0 text-white/40 transition group-hover:translate-x-0.5 group-hover:text-gold" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
}
